import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Shield, RefreshCw, Target, Clock, Users, CheckCircle2 } from 'lucide-react'
import SEO from '../../components/SEO'

const ProjectRecovery = () => {
  const features = [
    {
      icon: RefreshCw,
      title: 'Rapid Assessment',
      description: 'Swift, comprehensive analysis of troubled projects to identify root causes and critical issues.'
    },
    {
      icon: Target,
      title: 'Strategic Recovery Plan',
      description: 'Tailored recovery strategies designed to get derailed technology initiatives back on track.'
    },
    {
      icon: Clock,
      title: 'Immediate Mobilization',
      description: 'Expert teams deployed quickly to stabilize situations and prevent further issues.'
    },
    {
      icon: Users,
      title: 'Experienced Leadership',
      description: 'Seasoned project recovery specialists with proven track records in complex situations.'
    },
    {
      icon: CheckCircle2,
      title: 'Successful Delivery',
      description: 'Focus on achieving project objectives and delivering value, not just fixing problems.'
    },
    {
      icon: Shield,
      title: 'Confidential Handling',
      description: 'Discreet management of sensitive situations with complete privacy and professionalism.'
    }
  ]

  return (
    <>
      <SEO 
        title="Project Recovery - Praetorian Works"
        description="Elite project recovery services for HNW and UHNW clients. Expert rescue of troubled technology initiatives with discretion and proven results."
        keywords="project recovery, technology rescue, failed project recovery, IT project turnaround"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent"></div>
          
          <div className="container-max relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl mb-8 shadow-2xl">
                <Shield className="w-10 h-10 text-secondary-900" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Project
                <span className="text-primary-500 ml-4">Recovery</span>
              </h1>
              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                When technology projects go off track, we step in with expert rescue services. 
                Swift assessment, strategic recovery, and proven methodologies to salvage troubled 
                initiatives and deliver successful outcomes.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="bg-gradient-to-br from-secondary-800 to-secondary-900 rounded-2xl p-8 border border-neutral-800 hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-6 shadow-lg">
                    <feature.icon className="w-8 h-8 text-secondary-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Expertise */}
        <section className="py-20 bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Turn Failure Into <span className="text-primary-500">Success</span>
              </h2>
              <p className="text-lg text-neutral-300 mb-6 leading-relaxed">
                Technology projects can fail for many reasons—poor planning, inadequate resources, scope creep, 
                vendor issues, or simply bad execution. When you have a troubled initiative consuming resources 
                without delivering results, you need specialists who can quickly assess, stabilize, and recover.
              </p>
              <p className="text-lg text-neutral-300 leading-relaxed">
                Our project recovery experts bring decades of experience rescuing complex technology initiatives. 
                We've seen it all and know how to identify true blockers, cut through complexity, and implement 
                practical solutions that deliver results. We handle even the most sensitive situations with 
                complete discretion and professionalism.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Rescue Your Project
              </h2>
              <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
                Don't let a troubled project continue draining resources. Get expert help now.
              </p>
              <Link to="/contact" className="btn-primary text-lg">
                Request Assessment
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ProjectRecovery
